import { Phone, PhoneOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';

import { useVoiceStore } from '../../state/voice-ui.store';
import { callStatusMeta } from '../../utils/call-status-meta';
import { CallTimer } from './call-timer';

/**
 * The call widget's fixed launcher in the app header (next to the
 * notification bell) — it replaced the draggable minimized pill. Three
 * states:
 *   1. idle: a square icon button that opens/closes the widget panel;
 *   2. ringing while the panel is closed: expands inline with the caller and
 *      answer/decline (when the panel is open, the in-panel RingingQueue owns
 *      those actions — the badge stays a plain toggle);
 *   3. active call: a compact live pill with the elapsed timer.
 * `useVoiceSession` (device + socket) is mounted once, in CallWidget — so
 * answer/decline publish a `ringingAction` request through the store which
 * the widget consumes (same bridge pattern as `dialRequest`).
 */
export function CallHeaderBadge() {
    const { t } = useTranslation();
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canUseVoice = hasAnyPermission([
        PERMISSION.VOICE_CALLS_MAKE,
        PERMISSION.VOICE_CALLS_RECEIVE,
    ]);
    // Mirrors CallWidget: only receive-capable users register a softphone, so
    // only for them does "offline" mean missed calls.
    const canReceive = hasAnyPermission([PERMISSION.VOICE_CALLS_RECEIVE]);

    const minimized = useVoiceStore((s) => s.minimized);
    const ringingQueue = useVoiceStore((s) => s.ringingQueue);
    const activeCall = useVoiceStore((s) => s.activeCall);
    const deviceStatus = useVoiceStore((s) => s.deviceStatus);
    const deviceError = useVoiceStore((s) => s.deviceError);
    const setMinimized = useVoiceStore((s) => s.setMinimized);
    const requestRingingAction = useVoiceStore((s) => s.requestRingingAction);

    if (permissionsLoading || !canUseVoice) return null;

    const offline = canReceive && deviceStatus === 'offline';
    // Oldest first — finish-first order, same as the in-panel queue.
    const firstRinging = ringingQueue[0];

    if (activeCall) {
        return (
            <button
                type="button"
                data-slot="voice-header-badge"
                aria-pressed={!minimized}
                aria-label={t('voice.headerBadge.onCall', 'Ongoing call — open call widget')}
                onClick={() => setMinimized(!minimized)}
                className={cn(
                    'flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-white transition-colors focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none',
                    activeCall.onHold
                        ? 'bg-warning-500 hover:bg-warning-600'
                        : 'bg-primary-500 hover:bg-primary-600',
                )}
            >
                <Phone className="size-3.5" />
                {/* No elapsed time before the far end picks up — until then the
                    badge names the state ("Ringing…") instead of counting
                    ringback as call duration. */}
                {activeCall.answeredAt ? (
                    <CallTimer
                        key={activeCall.callId}
                        answeredAt={activeCall.answeredAt}
                        className="tabular-nums"
                    />
                ) : (
                    <span>{callStatusMeta(activeCall.status).label}</span>
                )}
                {ringingQueue.length > 0 && (
                    <span className="relative flex size-2" aria-hidden>
                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-400 opacity-75" />
                        <span className="relative inline-flex size-2 rounded-full bg-success-500" />
                    </span>
                )}
            </button>
        );
    }

    if (minimized && firstRinging) {
        const caller = firstRinging.contact?.name ?? firstRinging.fromNumber;
        return (
            <div
                data-slot="voice-header-badge-ringing"
                className="flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-success-500/40 bg-success-500/10 pr-1 pl-2 dark:bg-success-500/[0.12]"
            >
                <span className="relative flex size-2 shrink-0" aria-hidden>
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-success-400 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-success-500" />
                </span>
                <button
                    type="button"
                    title={`${t('voice.headerBadge.incoming', 'Incoming call')} · ${firstRinging.fromNumber}`}
                    onClick={() => setMinimized(false)}
                    className="max-w-40 min-w-0 truncate text-xs font-medium text-text-primary focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                >
                    {caller}
                </button>
                {ringingQueue.length > 1 && (
                    <span className="shrink-0 rounded-full bg-success-500/15 px-1.5 text-[10px] font-semibold text-success-700 dark:text-success-400">
                        +{ringingQueue.length - 1}
                    </span>
                )}
                <Button
                    type="button"
                    variant="destructive"
                    size="icon-xs"
                    aria-label={t('voice.headerBadge.decline', 'Decline call from {{caller}}', {
                        caller,
                    })}
                    className="rounded-full shadow-sm"
                    onClick={() =>
                        requestRingingAction({ action: 'decline', callId: firstRinging.callId })
                    }
                >
                    <PhoneOff className="size-3" />
                </Button>
                <Button
                    type="button"
                    size="icon-xs"
                    aria-label={t('voice.headerBadge.answer', 'Answer call from {{caller}}', {
                        caller,
                    })}
                    className="rounded-full bg-success-500 text-white hover:bg-success-600"
                    onClick={() =>
                        requestRingingAction({ action: 'answer', callId: firstRinging.callId })
                    }
                >
                    <Phone className="size-3" />
                </Button>
            </div>
        );
    }

    return (
        <Button
            variant="outline"
            size="icon-sm"
            data-slot="voice-header-badge"
            aria-pressed={!minimized}
            aria-label={
                minimized
                    ? t('voice.headerBadge.open', 'Open call widget')
                    : t('voice.headerBadge.close', 'Close call widget')
            }
            className={cn('relative', !minimized && 'bg-accent text-accent-foreground')}
            onClick={() => setMinimized(!minimized)}
        >
            <Phone className="size-4.5" />
            {offline && (
                // Red + steady when the reason is a config problem retrying
                // won't fix; amber + pulsing while genuinely reconnecting.
                <span
                    data-slot="voice-header-badge-offline"
                    title={deviceError ?? undefined}
                    className={cn(
                        'absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-background',
                        deviceError ? 'bg-error-500' : 'animate-pulse bg-warning-500',
                    )}
                />
            )}
        </Button>
    );
}
